// Phase 1 Executive Snapshot — deterministic, evidence-based, client-side.
//
// Every brief is a pure function of the current MetricsPayload. No network
// calls, no OpenAI, no fabrication. If a signal is missing we say so — a
// "Not enough data" state is a first-class outcome, not a fallback.
//
// The nine agents are named characters, not model prompts. Phase 2 will
// hand these briefs to a Founder AI (Ava) that composes a single daily
// summary; keep each builder pure so that step is trivial.

import type { MetricsPayload } from './metrics';

// ── Types ───────────────────────────────────────────────────────────────────

export type AgentSeverity =
  | 'healthy'
  | 'watch'
  | 'needs_attention'
  | 'critical'
  | 'no_data';

export interface ExecutiveAgentBrief {
  id:             string;
  name:           string;
  title:          string;
  severity:       AgentSeverity;
  severityLabel:  string;
  score:          number | null;   // 0–100, null when severity is 'no_data'
  // Phase 1 fields — kept for backwards compatibility.
  summary:        string;
  risk:           string;
  action:         string;
  confidencePct:  number;          // 0–100
  evidence:       string[];        // ≤ 3 bullets
  // Phase 4B extensions — all optional.
  situation?:      string;
  likelyCause?:    string;
  riskIfIgnored?:  string;
  nextCheckpoint?: string;
  incidentState?:  IncidentState;
}

export type IncidentState =
  | 'none'
  | 'watch'
  | 'candidate'
  | 'critical_candidate';

// ── Small helpers ───────────────────────────────────────────────────────────

const SEVERITY_LABEL: Record<AgentSeverity, string> = {
  healthy:         'Healthy',
  watch:           'Watch',
  needs_attention: 'Needs attention',
  critical:        'Critical',
  no_data:         'Not enough data',
};

/** Coarse baseline score by severity; individual builders can override. */
function baselineScore(severity: AgentSeverity): number | null {
  switch (severity) {
    case 'healthy':         return 92;
    case 'watch':           return 72;
    case 'needs_attention': return 45;
    case 'critical':        return 18;
    case 'no_data':         return null;
  }
}

function brief(input: {
  id: string;
  name: string;
  title: string;
  severity: AgentSeverity;
  summary: string;
  risk: string;
  action: string;
  confidencePct: number;
  evidence: string[];
  score?: number | null;
  situation?: string;
  likelyCause?: string;
  riskIfIgnored?: string;
  nextCheckpoint?: string;
  incidentState?: IncidentState;
}): ExecutiveAgentBrief {
  return {
    id:            input.id,
    name:          input.name,
    title:         input.title,
    severity:      input.severity,
    severityLabel: SEVERITY_LABEL[input.severity],
    score:         input.score ?? baselineScore(input.severity),
    summary:       input.summary,
    risk:          input.risk,
    action:        input.action,
    confidencePct: Math.max(0, Math.min(100, Math.round(input.confidencePct))),
    evidence:      input.evidence.slice(0, 3),
    situation:      input.situation      ?? input.summary,
    likelyCause:    input.likelyCause    ?? defaultLikelyCause(input.severity),
    riskIfIgnored:  input.riskIfIgnored  ?? input.risk,
    nextCheckpoint: input.nextCheckpoint ?? defaultCheckpoint(input.severity),
    incidentState:  input.incidentState,
  };
}

function defaultCheckpoint(severity: AgentSeverity): string {
  switch (severity) {
    case 'critical':         return 'Reassess within the next hour.';
    case 'needs_attention':  return 'Reassess by end of day today.';
    case 'watch':            return 'Reassess this week.';
    case 'healthy':          return 'Reassess at the next daily briefing.';
    case 'no_data':          return 'Reassess once telemetry begins arriving.';
  }
}

function defaultLikelyCause(severity: AgentSeverity): string {
  switch (severity) {
    case 'critical':         return 'A confirmed regression is emitting critical signal in the current window.';
    case 'needs_attention':  return 'Repeated signal in the current window points to a specific issue this agent owns.';
    case 'watch':            return 'A soft trend is drifting; no confirmed regression yet.';
    case 'healthy':          return 'Current signal is within the expected operating envelope.';
    case 'no_data':          return 'Upstream telemetry for this agent has not fired yet.';
  }
}

function countBySeverity(alerts: MetricsPayload['alerts']) {
  let critical = 0;
  let warn     = 0;
  let info     = 0;
  for (const a of alerts) {
    const s = (a.severity ?? '').toUpperCase();
    if      (s === 'CRITICAL') critical++;
    else if (s === 'WARN' || s === 'HIGH') warn++;
    else if (s === 'INFO' || s === 'LOW')  info++;
  }
  return { critical, warn, info, total: alerts.length };
}

function fmtPct(n: number): string {
  return `${n.toFixed(0)}%`;
}
function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

// ── Individual agent builders ───────────────────────────────────────────────

export function buildSentinelBrief(m: MetricsPayload): ExecutiveAgentBrief {
  // security is typed `any` on MetricsPayload; narrow inline.
  const sec        = m.security ?? {};
  const states     = sec.accountStates      ?? { ACTIVE: 0, COOLDOWN: 0, UNDER_REVIEW: 0, BANNED: 0 };
  const risk       = sec.riskDistribution   ?? { low: 0, medium: 0, high: 0, critical: 0 };
  const events24h  = sec.last24hEvents      ?? { total: 0, critical: 0, suspicious: 0 };
  const banned     = Number(states.BANNED       ?? 0);
  const underRev   = Number(states.UNDER_REVIEW ?? 0);
  const cooldown   = Number(states.COOLDOWN     ?? 0);
  const criticalEvts   = Number(events24h.critical   ?? 0);
  const suspiciousEvts = Number(events24h.suspicious ?? 0);
  const criticalUsers  = Number(risk.critical ?? 0);
  const highUsers      = Number(risk.high     ?? 0);

  const alertCounts = countBySeverity(m.alerts.filter((a) => (a.category ?? '').toLowerCase().includes('security')));

  let severity: AgentSeverity = 'healthy';
  let summary = 'No banned or under-review accounts and no critical security events in the last 24h.';
  let risk_   = 'None material in the current window.';
  let action  = 'Keep the current guardrails; no change required today.';

  if (criticalEvts > 0 || alertCounts.critical > 0 || banned > 0) {
    severity = 'needs_attention';
    summary  = 'Active abuse or ban signal — Sentinel is treating this as an intervention window.';
    risk_    = 'A confirmed abuse pattern can escalate silently if not triaged today.';
    action   = 'Open the mobile Security section and review the banned / under-review users, then acknowledge or close the associated critical alerts.';
  } else if (underRev > 0 || suspiciousEvts > 5 || criticalUsers > 0) {
    severity = 'watch';
    summary  = 'A small number of accounts are in review or on the elevated-risk shelf.';
    risk_    = 'Under-review accounts can lock legitimate users out if left there too long.';
    action   = 'Sweep the under-review queue this week and clear or escalate each one.';
  }

  const evidence: string[] = [
    `Accounts: ${banned} banned · ${underRev} under review · ${cooldown} cooldown`,
    `Risk score buckets: ${criticalUsers} critical · ${highUsers} high · ${Number(risk.medium ?? 0)} medium`,
    `Last 24h events: ${criticalEvts} critical · ${suspiciousEvts} suspicious · total ${Number(events24h.total ?? 0)}`,
  ];

  const likelyCause =
    severity === 'healthy'         ? 'Guardrails are absorbing the current threat model without escalation.' :
    severity === 'watch'           ? 'A small elevated-risk cohort has not been cleared from review.' :
                                     'Confirmed abuse pattern from one or more users.';
  const nextCheckpoint =
    severity === 'needs_attention' ? 'Reassess after triaging the flagged accounts today.' :
    severity === 'watch'           ? 'Reassess when the under-review queue is next swept.' :
                                     defaultCheckpoint(severity);
  return brief({
    id: 'sentinel', name: 'Sentinel', title: 'Chief Security Officer',
    severity, summary, risk: risk_, action,
    confidencePct: 88, evidence,
    likelyCause, nextCheckpoint,
  });
}

export function buildAtlasBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const f = m.customerHealth.onboardingFunnel;

  if (!f.available) {
    return brief({
      id: 'atlas', name: 'Atlas', title: 'Head of Growth',
      severity: 'no_data',
      summary: 'Onboarding telemetry has not been observed yet.',
      risk: 'Without funnel signal we can\'t see where new installs drop off — every product decision is guesswork until this fires.',
      action: 'Open the latest preview build and complete onboarding once end-to-end to seed telemetry.',
      confidencePct: 60,
      evidence: [f.reason],
    });
  }

  const started    = f.installsStarted;
  const completed  = f.installsCompleted;
  const rate       = f.completionRatePct;
  const tapVsMock  = f.googleConnectMockedOrBlockedTotal / Math.max(1, f.googleConnectTapsTotal);

  let severity: AgentSeverity = 'healthy';
  let summary = `${completed} of ${started} onboardings completed in the last 30 days` +
                (rate != null ? ` (${rate}% conversion).` : '.');
  let risk_   = 'None material at current volumes.';
  let action  = 'Keep monitoring — first meaningful cohort still assembling.';

  if (started === 0) {
    severity = 'no_data';
    summary  = 'No onboarding starts recorded in the last 30 days.';
    risk_    = 'Either no installs, or telemetry has regressed — both are worth checking.';
    action   = 'Open the latest preview build and complete onboarding once to prove the pipe.';
  } else if (rate != null && rate < 40) {
    severity = 'needs_attention';
    summary  = `Only ${rate}% of onboarding starts are completing (${completed}/${started}).`;
    risk_    = 'A sub-40% completion rate wastes every install we buy or earn.';
    action   = 'Walk through onboarding on a fresh account; identify the step where the drop-off lands.';
  } else if (rate != null && rate < 70) {
    severity = 'watch';
    summary  = `${rate}% onboarding completion — room to improve but not urgent.`;
    risk_    = 'Silent friction in mid-funnel steps.';
    action   = 'Add a light drop-off review to the next release cycle.';
  } else if (tapVsMock > 0.5 && f.googleConnectTapsTotal >= 5) {
    severity = 'watch';
    summary  = 'Most Google-connect taps are still hitting the mock/blocked path.';
    risk_    = 'Users see the CTA but can\'t actually connect their business yet.';
    action   = 'Land GBP_OAUTH_ENABLED = true or ship a clearer "coming soon" state.';
  }

  return brief({
    id: 'atlas', name: 'Atlas', title: 'Head of Growth',
    severity, summary, risk: risk_, action,
    confidencePct: 82,
    evidence: [
      `Starts → completed: ${started} → ${completed}` + (rate != null ? ` (${rate}%)` : ''),
      `First review imported: ${f.firstReviewCount} · first reply generated: ${f.firstReplyCount}`,
      `Google connect taps: ${f.googleConnectTapsTotal} (${f.googleConnectMockedOrBlockedTotal} mocked/blocked)`,
    ],
  });
}

export function buildPulseBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const ch = m.customerHealth;
  const active7  = ch.activeUsersSevenDays;
  const active30 = ch.activeUsersThirtyDays;
  const gen      = ch.repliesGeneratedSeven;
  const posted   = ch.repliesPostedSeven;
  const postRate = gen > 0 ? Math.round((posted / gen) * 100) : null;

  let severity: AgentSeverity = 'healthy';
  let summary = `${active7} active users this week generated ${gen} replies (${posted} posted).`;
  let risk_   = 'None material — engagement is trending as expected.';
  let action  = 'Keep watching post-rate; investigate if it falls below 60%.';

  if (active7 === 0 && active30 === 0 && gen === 0) {
    severity = 'no_data';
    summary  = 'No user activity yet. Expected during pre-launch before external onboarding.';
    risk_    = 'None — this becomes meaningful once real users are installed.';
    action   = 'Revisit once the first cohort is live.';
  } else if (gen > 0 && postRate != null && postRate < 40) {
    severity = 'needs_attention';
    summary  = `Only ${postRate}% of generated replies are being posted (${posted}/${gen}).`;
    risk_    = 'Users are generating drafts but not shipping them — either the send flow is broken or the copy isn\'t landing.';
    action   = 'Sample 10 recent unsent drafts and figure out why they stalled.';
  } else if (gen > 0 && postRate != null && postRate < 60) {
    severity = 'watch';
    summary  = `Post-rate at ${postRate}% (${posted}/${gen}).`;
    risk_    = 'Some post-send friction in the deck.';
    action   = 'Log the last step users see before abandoning a draft.';
  }

  return brief({
    id: 'pulse', name: 'Pulse', title: 'Customer Success',
    severity, summary, risk: risk_, action,
    confidencePct: 78,
    evidence: [
      `Active users: ${active7} (7d) · ${active30} (30d)`,
      `Replies generated (7d): ${gen}`,
      `Replies posted (7d): ${posted}` + (postRate != null ? ` (${postRate}% of generated)` : ''),
    ],
  });
}

export function buildForgeBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const infra = m.infrastructure ?? {};
  const errorRatePct: number     = Number(infra.errorRatePct ?? 0);
  const slowest: any[]           = Array.isArray(infra.slowestEndpoints) ? infra.slowestEndpoints : [];
  const openaiToday              = m.openai?.today ?? {};
  const openaiSuccessRate        = Number(openaiToday.successRatePct ?? 100);
  const openaiLatency            = Number(openaiToday.avgLatencyMs ?? 0);
  const googleAvgLatency         = Number(m.google?.averageLatencyMs ?? 0);
  const worstLatency             = slowest[0]?.avgLatencyMs ?? 0;

  let severity: AgentSeverity = 'healthy';
  let summary = `Error rate ${errorRatePct}% over the last 7 days — no fires today.`;
  let risk_   = 'Latency-tail regressions are hard to see without a synthetic probe.';
  let action  = 'Keep reviewing the slowest-endpoints table weekly.';

  if (errorRatePct > 5 || openaiSuccessRate < 90) {
    severity = 'needs_attention';
    summary  = `Elevated failures — ${errorRatePct}% edge error rate, ${openaiSuccessRate}% OpenAI success.`;
    risk_    = 'Every failed call at this stage is a lost first impression.';
    action   = 'Open the mobile Infrastructure section and check the top failing function first.';
  } else if (errorRatePct > 1 || worstLatency > 3000) {
    severity = 'watch';
    summary  = `Error rate ${errorRatePct}%, slowest endpoint ${worstLatency}ms.`;
    risk_    = 'The tail is trending; not fires yet.';
    action   = 'Check the slowest endpoint and confirm it\'s expected to be slow.';
  }

  return brief({
    id: 'forge', name: 'Forge', title: 'Platform Engineer',
    severity, summary, risk: risk_, action,
    confidencePct: 84,
    evidence: [
      `Edge error rate (7d): ${errorRatePct}%`,
      `OpenAI success today: ${openaiSuccessRate}% · avg latency ${openaiLatency}ms`,
      `Slowest endpoint: ${slowest[0]?.functionName ?? '—'} @ ${worstLatency}ms · Google avg ${googleAvgLatency}ms`,
    ],
  });
}

export function buildEchoBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const loc = m.localization;

  if (!loc.available) {
    return brief({
      id: 'echo', name: 'Echo', title: 'Localization Lead',
      severity: 'no_data',
      summary: 'No i18n runtime telemetry observed.',
      risk: 'Could mean English-only usage, or that no client has shipped this build. Both are plausible.',
      action: 'Confirm the ops-events pipeline is receiving `client:i18n_fallback` events after the next preview install.',
      confidencePct: 55,
      evidence: [loc.reason],
    });
  }

  const missing   = loc.missingTotal;
  const fallbacks = loc.fallbackTotal;
  const users     = loc.affectedUsers;
  const topLang   = loc.fallbackByLang[0]?.label ?? '—';

  let severity: AgentSeverity = 'healthy';
  let summary = `No missing keys and no fallbacks in the last 30 days.`;
  let risk_   = 'None material — coverage is holding.';
  let action  = 'No change required.';

  if (missing > 0) {
    severity = 'needs_attention';
    summary  = `${missing} missing-key events across ${users} users — English string absent entirely.`;
    risk_    = 'A missing English key ships raw dot-path text to users. This is a shipping bug.';
    action   = `Add English strings for the top missing keys, starting with "${loc.topMissingKeys[0]?.label ?? '—'}".`;
  } else if (fallbacks > 0) {
    severity = 'watch';
    summary  = `${fallbacks} locale fallbacks (${topLang} leads) affecting ${users} users.`;
    risk_    = 'Non-English users see English strings mid-flow, breaking immersion.';
    action   = `Translate the top fallback key first: "${loc.topFallbackKeys[0]?.label ?? '—'}".`;
  }

  return brief({
    id: 'echo', name: 'Echo', title: 'Localization Lead',
    severity, summary, risk: risk_, action,
    confidencePct: 86,
    evidence: [
      `Fallbacks (30d): ${fallbacks} · missing: ${missing}`,
      `Users affected: ${users}`,
      loc.fallbackByLang.length > 0
        ? `Top language: ${topLang} (${loc.fallbackByLang[0].count})`
        : 'No language-level breakdown yet',
    ],
  });
}

export function buildLedgerBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const rev       = m.revenue;
  const ex        = m.executive;
  const mrr       = rev.mrrUsd;
  const dailyRev  = ex.todayRevenueUsd;
  const aiCost    = ex.todayAiCostUsd;
  const margin    = ex.grossMarginPct;

  let severity: AgentSeverity = 'healthy';
  let summary = `MRR ${fmtUsd(mrr)} · today\'s AI spend ${fmtUsd(aiCost)}.`;
  let risk_   = 'None material — we\'re inside the expected cost envelope.';
  let action  = 'Continue watching per-active AI cost weekly.';

  if (mrr === 0 && dailyRev === 0 && aiCost === 0) {
    severity = 'no_data';
    summary  = 'Pre-revenue with no AI spend today. Expected before external launch.';
    risk_    = 'None — this becomes meaningful once traffic exists.';
    action   = 'Revisit after the first cohort of external users signs up.';
  } else if (mrr === 0 && aiCost > 0) {
    severity = 'watch';
    summary  = `Pre-revenue but AI cost today is ${fmtUsd(aiCost)}.`;
    risk_    = 'Every dollar spent before revenue lands is founder-funded runway.';
    action   = 'Confirm the spend is from real testing (not a runaway loop) and note it against burn.';
  } else if (mrr > 0 && margin < 30) {
    severity = 'needs_attention';
    summary  = `Gross margin ${fmtPct(margin)} — thin for a SaaS at this scale.`;
    risk_    = 'AI cost per active user is eating the plan pricing.';
    action   = 'Review per-active AI cost in the scale simulator and consider raising pricing tiers or capping generations.';
  }

  return brief({
    id: 'ledger', name: 'Ledger', title: 'Finance Lead',
    severity, summary, risk: risk_, action,
    confidencePct: 84,
    evidence: [
      `MRR ${fmtUsd(mrr)} · projected ARR ${fmtUsd(rev.arrUsd)}`,
      `Today: revenue ${fmtUsd(dailyRev)} · AI cost ${fmtUsd(aiCost)} · margin ${fmtPct(margin)}`,
      `Paying plans: ${rev.counts.core} core · ${rev.counts.pro} pro · ${rev.counts.lifetime} lifetime`,
    ],
  });
}

export function buildLaunchBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const rh    = m.releaseHealth;
  const crash = m.crashCenter;
  const sentryArmed = crash.clientConfigured === true;
  const discoverReady = crash.discoverReady === true;

  let severity: AgentSeverity = 'healthy';
  let summary = 'Release telemetry armed and crash reporting configured.';
  let risk_   = 'None material.';
  let action  = 'Consider wiring the Sentry Discover API next so server-side aggregates land here.';

  if (!sentryArmed) {
    severity = 'needs_attention';
    summary  = 'Sentry DSN not configured server-side — crash reporting can\'t be verified from this dashboard.';
    risk_    = 'A ship-blocking crash in a preview build would be invisible until a user reports it.';
    action   = 'Set SENTRY_DSN (or EXPO_PUBLIC_SENTRY_DSN) in Supabase Edge Function secrets and redeploy admin-metrics.';
  } else if (!rh.available) {
    severity = 'watch';
    summary  = 'Sentry armed, but no `release_seen` events observed — build adoption is unknown.';
    risk_    = 'Without release_seen we can\'t tell if the latest build is actually running.';
    action   = 'Reinstall the latest preview build on one device and confirm release_seen fires.';
  } else if (rh.currentBuild && rh.currentBuild.distinctUsers <= 1 && rh.versions.length > 1) {
    severity = 'watch';
    summary  = `Newest build (${rh.currentBuild.appVersion}) has only ${rh.currentBuild.distinctUsers} user(s); older builds still dominate.`;
    risk_    = 'A slow rollout may mean update prompts aren\'t reaching users.';
    action   = 'Verify the update channel and force-refresh on stale devices.';
  }

  const cbLine = rh.available && rh.currentBuild
    ? `Current build: ${rh.currentBuild.appVersion} (${rh.currentBuild.buildNumber}) · ${rh.currentBuild.distinctUsers} users`
    : rh.available
      ? 'Current build: —'
      : 'Release telemetry: not yet observed';
  return brief({
    id: 'launch', name: 'Launch', title: 'Release Manager',
    severity, summary, risk: risk_, action,
    confidencePct: 80,
    evidence: [
      cbLine,
      `Sentry client: ${sentryArmed ? 'armed' : 'not configured'} · Discover API: ${discoverReady ? 'ready' : 'not wired'}`,
      rh.available ? `Distinct builds seen (30d): ${rh.versions.length}` : 'No build history yet',
    ],
  });
}

export function buildWatchtowerBrief(m: MetricsPayload): ExecutiveAgentBrief {
  const alerts = countBySeverity(m.alerts);
  const sentryArmed = m.crashCenter.clientConfigured === true;

  let severity: AgentSeverity = 'healthy';
  let summary = 'No open alerts and crash reporting is armed.';
  let risk_   = 'None material at the moment.';
  let action  = 'Stand down — nothing needs the founder right now.';

  if (alerts.critical > 0) {
    severity = 'critical';
    summary  = `${alerts.critical} critical alert${alerts.critical > 1 ? 's' : ''} open — page-worthy.`;
    risk_    = 'Critical alerts indicate a system that has already regressed; every hour of delay compounds.';
    action   = 'Open the Alerts page now and triage the critical items before anything else.';
  } else if (alerts.warn > 0) {
    severity = 'needs_attention';
    summary  = `${alerts.warn} warning-level alert${alerts.warn > 1 ? 's' : ''} open.`;
    risk_    = 'Warnings tend to become criticals when ignored.';
    action   = 'Sweep the Alerts page this session and acknowledge or close each item.';
  } else if (!sentryArmed) {
    severity = 'watch';
    summary  = 'No open alerts, but crash reporting is not armed — silent failures possible.';
    risk_    = 'A crash-loop in a preview build would not raise an alert here.';
    action   = 'Configure Sentry DSN in Supabase secrets to close the blind spot.';
  }

  // Phase 4E: deterministic incident classification.
  let incidentState: IncidentState = 'none';
  if (alerts.critical > 0) {
    incidentState = alerts.critical >= 3 ? 'critical_candidate' : 'candidate';
  } else if (alerts.warn >= 3) {
    incidentState = 'candidate';
  } else if (alerts.warn > 0 || !sentryArmed) {
    incidentState = 'watch';
  }

  return brief({
    id: 'watchtower', name: 'Watchtower', title: 'Incident Commander',
    severity, summary, risk: risk_, action,
    confidencePct: 90,
    evidence: [
      `Open alerts: ${alerts.total} (${alerts.critical} critical · ${alerts.warn} warn · ${alerts.info} info)`,
      `Crash reporting: ${sentryArmed ? 'armed' : 'not configured'}`,
      `Last scan window: recent (from admin-metrics)`,
    ],
    incidentState,
  });
}

// Ava synthesizes the other briefs into a single founder read. Runs LAST so
// it can inspect the other severities.
export function buildAvaBrief(
  m: MetricsPayload,
  peers: ExecutiveAgentBrief[],
): ExecutiveAgentBrief {
  const overall = Number(m.executive.overallHealth ?? 0);
  const worst   = pickWorst(peers);
  const criticalPeers = peers.filter((p) => p.severity === 'critical');
  const needsPeers    = peers.filter((p) => p.severity === 'needs_attention');
  const noDataPeers   = peers.filter((p) => p.severity === 'no_data');

  let severity: AgentSeverity;
  let summary: string;
  let risk_: string;
  let action: string;

  if (criticalPeers.length > 0) {
    severity = 'critical';
    summary  = `Overall health ${overall}. ${criticalPeers[0].name} is flagging a critical situation.`;
    risk_    = criticalPeers[0].risk;
    action   = criticalPeers[0].action;
  } else if (needsPeers.length > 0) {
    severity = 'needs_attention';
    summary  = `Overall health ${overall}. ${needsPeers.length} area${needsPeers.length > 1 ? 's need' : ' needs'} attention today.`;
    risk_    = needsPeers[0].risk;
    action   = needsPeers[0].action;
  } else if (overall >= 85 && noDataPeers.length <= 2) {
    severity = 'healthy';
    summary  = 'ReVloom is operationally healthy. No immediate founder intervention is required.';
    risk_    = noDataPeers.length > 0
      ? `Some launch-health signals are still not instrumented (${noDataPeers.map((p) => p.name).join(', ')}).`
      : 'None material at present.';
    action   = noDataPeers.length > 0
      ? `Seed missing telemetry: ${noDataPeers[0].action}`
      : 'Keep working the roadmap; check back tomorrow.';
  } else if (noDataPeers.length >= 3) {
    severity = 'watch';
    summary  = `Overall health ${overall}, but ${noDataPeers.length} agents lack data.`;
    risk_    = 'Decisions taken now are running on partial signal.';
    action   = `Seed the missing telemetry first — start with ${noDataPeers[0].name}: ${noDataPeers[0].action}`;
  } else {
    severity = 'watch';
    summary  = `Overall health ${overall}. ${worst?.name ?? 'Watchtower'} is the strongest voice today.`;
    risk_    = worst?.risk ?? 'Trends are drifting; nothing has broken.';
    action   = worst?.action ?? 'Continue monitoring; no forced intervention needed today.';
  }

  return brief({
    id: 'ava', name: 'Ava', title: 'Chief of Staff',
    severity, summary, risk: risk_, action,
    confidencePct: 82,
    evidence: [
      `Overall health score: ${overall}`,
      `Team: ${peers.filter((p) => p.severity === 'healthy').length} healthy · ${needsPeers.length + criticalPeers.length} flagged · ${noDataPeers.length} no data`,
      worst ? `Highest-signal voice: ${worst.name} (${worst.severityLabel})` : 'No standout signal',
    ],
    score: overall,
  });
}

// Ordered worst → best; used by Ava to pick the highest-signal voice.
function pickWorst(peers: ExecutiveAgentBrief[]): ExecutiveAgentBrief | null {
  const rank: Record<AgentSeverity, number> = {
    critical: 0, needs_attention: 1, watch: 2, no_data: 3, healthy: 4,
  };
  const sorted = [...peers].sort((a, b) => rank[a.severity] - rank[b.severity]);
  return sorted[0] ?? null;
}

// ── Public entry point ──────────────────────────────────────────────────────

export function buildExecutiveSnapshot(m: MetricsPayload): ExecutiveAgentBrief[] {
  const sentinel   = buildSentinelBrief(m);
  const atlas      = buildAtlasBrief(m);
  const pulse      = buildPulseBrief(m);
  const forge      = buildForgeBrief(m);
  const echo       = buildEchoBrief(m);
  const ledger     = buildLedgerBrief(m);
  const launch     = buildLaunchBrief(m);
  const watchtower = buildWatchtowerBrief(m);
  const peers = [sentinel, atlas, pulse, forge, echo, ledger, launch, watchtower];
  const ava = buildAvaBrief(m, peers);
  // Ava first, then Watchtower (incident-commander), then domain leads.
  return [ava, watchtower, sentinel, atlas, pulse, forge, echo, ledger, launch];
}
